import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Activity, History, Settings as SettingsIcon, Zap, LogIn, LogOut, Sparkles } from 'lucide-react'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { authApi } from '@/api/client'

export default function Navbar() {
  const { pathname } = useLocation()
  const { data: user } = useCurrentUser()
  const isAuthenticated = !!user

  return (
    <header className="glass sticky top-0 z-50 px-6 py-3 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2 group">
        <div className="relative">
          <Zap
            size={22}
            className="text-accent group-hover:drop-shadow-[0_0_8px_#6366f1] transition-all duration-300"
            fill="currentColor"
          />
        </div>
        <span className="font-bold text-base tracking-tight text-gradient">Lens</span>
      </Link>

      <nav className="flex items-center gap-1">
        <NavLink to="/" active={pathname === '/'} icon={<Activity size={15} />} label="Audit" />
        {/* Reports + Settings hidden until /api/me confirms an authenticated user —
            rendering them during the loading window caused a flicker for anonymous
            visitors. They're useless without a workspace anyway. */}
        {isAuthenticated && (
          <>
            <NavLink to="/reports" active={pathname.startsWith('/reports')} icon={<History size={15} />} label="Reports" />
            <NavLink to="/settings" active={pathname === '/settings'} icon={<SettingsIcon size={15} />} label="Settings" />
          </>
        )}
        {/* Subscribe is always visible — anonymous users need a path to see the
            pitch, authenticated users need to manage their plan. */}
        <NavLink to="/subscribe" active={pathname === '/subscribe'} icon={<Sparkles size={15} />} label="Subscribe" />
        <UserPill />
      </nav>
    </header>
  )
}

function UserPill() {
  const { data: user, isLoading } = useCurrentUser()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Avoid flicker on first paint — keep the area empty until the probe lands.
  if (isLoading) return <div className="w-20 h-7 ml-2" />

  if (!user) {
    return (
      <Link
        to="/login"
        className="ml-2 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium
                   bg-accent/10 text-accent hover:bg-accent/20 transition-colors"
      >
        <LogIn size={15} />
        Sign in
      </Link>
    )
  }

  return (
    <div className="ml-2 flex items-center gap-1">
      <span
        title={user.email}
        className="px-3 py-1.5 rounded-md text-sm text-[var(--text-secondary)] bg-white/5
                   max-w-[180px] truncate"
      >
        {user.name || user.email}
      </span>
      <button
        type="button"
        onClick={async () => {
          await authApi.logout()
          // Drop the cached user immediately so the Navbar flips before next paint;
          // server-side the session cookie is already cleared.
          queryClient.setQueryData(['current-user'], null)
          navigate('/login')
        }}
        title="Sign out"
        className="p-1.5 rounded-md text-[var(--text-secondary)] hover:text-[var(--text-primary)]
                   hover:bg-white/5 transition-colors"
      >
        <LogOut size={15} />
      </button>
    </div>
  )
}

function NavLink({
  to,
  active,
  icon,
  label,
}: {
  to: string
  active: boolean
  icon: React.ReactNode
  label: string
}) {
  return (
    <Link
      to={to}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
        active
          ? 'bg-accent/10 text-accent'
          : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-white/5'
      }`}
    >
      {icon}
      {label}
    </Link>
  )
}
